import { useEffect, useState } from "react";
import { cn, formatEuro } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { useWorkspace } from "@/hooks/useWorkspace";

interface ClientData {
  id: string;
  name: string;
  revenue: number;
  orders: number;
  status: "premium" | "standard" | "new";
}

const statusStyles = {
  premium: "badge-status-success",
  standard: "badge-status-info",
  new: "badge-status-warning",
};

const statusLabels = {
  premium: "Premium",
  standard: "Estándar",
  new: "Nuevo",
};

export function TopClients() {
  const [clients, setClients] = useState<ClientData[]>([]);
  const [loading, setLoading] = useState(true);
  const { workspace } = useWorkspace();
  const workspaceId = workspace?.id || null;

  useEffect(() => {
    if (workspaceId) fetchTopClients();
  }, [workspaceId]);

  const fetchTopClients = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const { data: clientsData } = await supabase
        .from("clients")
        .select("id, name, company_name, status, created_at")
        .eq("workspace_id", workspaceId);

      if (!clientsData) {
        setClients([]);
        return;
      }

      const { data: ordersData } = await supabase
        .from("orders")
        .select("client_id, total")
        .eq("workspace_id", workspaceId);

      const { data: invoicesData } = await supabase
        .from("invoices")
        .select("client_id, total")
        .eq("workspace_id", workspaceId)
        .eq("status", "paid");

      const clientStats: Record<string, { revenue: number; orders: number }> = {};

      ordersData?.forEach((order) => {
        if (order.client_id) {
          if (!clientStats[order.client_id]) {
            clientStats[order.client_id] = { revenue: 0, orders: 0 };
          }
          clientStats[order.client_id].orders += 1;
        }
      });

      invoicesData?.forEach((invoice) => {
        if (invoice.client_id) {
          if (!clientStats[invoice.client_id]) {
            clientStats[invoice.client_id] = { revenue: 0, orders: 0 };
          }
          clientStats[invoice.client_id].revenue += Number(invoice.total) || 0;
        }
      });

      if (!invoicesData?.length) {
        ordersData?.forEach((order) => {
          if (order.client_id && clientStats[order.client_id]) {
            clientStats[order.client_id].revenue += Number(order.total) || 0;
          }
        });
      }

      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      const clientsWithStats: ClientData[] = clientsData.map((client) => {
        const stats = clientStats[client.id] || { revenue: 0, orders: 0 };
        const createdAt = new Date(client.created_at);
        
        let status: "premium" | "standard" | "new";
        if (createdAt > thirtyDaysAgo) {
          status = "new";
        } else if (stats.revenue >= 50000) {
          status = "premium";
        } else {
          status = "standard";
        }

        return {
          id: client.id,
          name: client.company_name || client.name,
          revenue: stats.revenue,
          orders: stats.orders,
          status,
        };
      });

      const topClients = clientsWithStats
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);

      setClients(topClients);
    } catch (error) {
      console.error("Error fetching top clients:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="chart-container animate-slide-in-up" style={{ animationDelay: "400ms" }}>
        <div className="mb-6">
          <h3 className="font-display text-lg font-semibold text-foreground">Top Clientes</h3>
          <p className="text-sm text-muted-foreground">Por ingresos generados</p>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-4"><Skeleton className="h-8 w-full" /></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container animate-slide-in-up" style={{ animationDelay: "400ms" }}>
      <div className="mb-6">
        <h3 className="font-display text-lg font-semibold text-foreground">Top Clientes</h3>
        <p className="text-sm text-muted-foreground">Por ingresos generados</p>
      </div>
      {clients.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">No hay clientes registrados</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Ingresos</th>
                <th>Órdenes</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client, index) => (
                <tr key={client.id} className="animate-slide-in-left" style={{ animationDelay: `${500 + index * 50}ms` }}>
                  <td className="font-medium">{client.name}</td>
                  <td>{formatEuro(client.revenue, false)}</td>
                  <td>{client.orders}</td>
                  <td>
                    <span className={cn("badge-status", statusStyles[client.status])}>
                      {statusLabels[client.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
